import React, { useEffect, useState, useRef } from 'react';
import { Box, Card, Typography, Button, Container, Avatar } from '@mui/material';
import { Phone, Email, Business } from '@mui/icons-material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore as db } from '../firebase.config';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import Cookies from 'js-cookie';

const MembershipCard = () => {
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        const userEmail = Cookies.get('userEmail');
        if (!userEmail) {
          setLoading(false);
          return;
        }

        const membersRef = collection(db, "MembershipMaster");
        const q = query(membersRef, where("personalEmail", "==", userEmail));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const memberDoc = querySnapshot.docs[0];
          const data = memberDoc.data();
          setMemberData({ 
            id: memberDoc.id,
            ...data,
            startDate: data.membershipStartDate || new Date().toISOString(),
            endDate: data.membershipEndDate || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()
          });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching member data:', error);
        setLoading(false);
      }
    };

    fetchMemberData();
  }, []);

  const downloadAsPDF = () => {
    if (!memberData) return;
    const pdf = new jsPDF('l', 'mm', [85, 54]);
    pdf.setFillColor(65, 105, 225);
    pdf.rect(0, 0, 85, 54, 'F');
    pdf.setTextColor(255, 255, 255);
    const memberSince = format(new Date(memberData.startDate), 'MMM yyyy');
    pdf.setFontSize(10);
    pdf.text(`Member Since: ${memberSince}`, 10, 10);
    pdf.setFontSize(18);
    pdf.setFont(undefined, 'bold');
    pdf.text(`${memberData.firstName} ${memberData.surname}`, 10, 18);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    pdf.text(`ID: ${memberData.id.slice(0, 8)}`, 10, 25);
    const bulletY = [31, 37, 43];
    for (let i = 0; i < 3; i++) {
      pdf.circle(11, bulletY[i], 0.8, 'F');
    }
    pdf.text(memberData.mobileNo, 15, bulletY[0]);
    pdf.text(memberData.personalEmail, 15, bulletY[1]);
    pdf.text(memberData.organisation || "OmneVu", 15, bulletY[2]);
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    pdf.text("OV", 65, 38);
    pdf.setFontSize(10);
    pdf.text("RETAIL", 63, 43);
    pdf.save('membership-card.pdf');
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!memberData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>No membership data found.</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your email address: {Cookies.get('userEmail')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Please check if you have an approved membership. If you've recently applied, your membership may still be pending approval.
        </Typography>
        <Typography variant="body2">
          If you believe this is an error, please contact support with the email you used for your membership application.
        </Typography>
        <Button 
          variant="contained" 
          component={Box} 
          sx={{ mt: 2 }}
          onClick={() => window.location.href = '/addMemberForm'}
        >
          Apply for Membership
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Card
          ref={cardRef}
          sx={{
            width: '100%',
            maxWidth: 340,
            height: 220,
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            p: 3
          }}
        >
          <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 500 }}>
            Member Since: {format(new Date(memberData.startDate), 'MMM yyyy')}
          </Typography>
          <Typography variant="h4" component="div" sx={{ mb: 0.5, fontWeight: 'bold', fontSize: 28 }}>
            {memberData.firstName} {memberData.surname}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            ID: {memberData.id.slice(0, 8)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Phone sx={{ color: 'white', mr: 1 }} />
            <Typography variant="body1">
              {memberData.mobileNo}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Email sx={{ color: 'white', mr: 1 }} />
            <Typography variant="body1" sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '220px'
            }}>
              {memberData.personalEmail}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Business sx={{ color: 'white', mr: 1 }} />
            <Typography variant="body1">
              {memberData.organisation || "OmneVu"}
            </Typography>
          </Box>
          <Avatar 
            src={memberData.photoURL || ""}
            sx={{ 
              width: 80, 
              height: 80, 
              border: '2px solid white',
              position: 'absolute',
              top: 20,
              right: 20,
              zIndex: 2,
              bgcolor: memberData.photoURL ? 'transparent' : 'grey.300'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 15,
              right: 20,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 'bold', letterSpacing: 1, lineHeight: 1 }}>
              OV
            </Typography>
            <Typography variant="h6" sx={{ letterSpacing: 2 }}>
              RETAIL
            </Typography>
          </Box>
        </Card>
        <Button 
          variant="contained" 
          onClick={downloadAsPDF}
          sx={{ 
            bgcolor: '#4169E1',
            px: 6,
            py: 1.5,
            borderRadius: 25,
            textTransform: 'uppercase',
            fontWeight: 'bold',
            fontSize: 16,
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            width: 'auto'
          }}
        >
          DOWNLOAD AS PDF
        </Button>
      </Box>
    </Container>
  );
};

export default MembershipCard; 